import React, { useState, useEffect, useCallback } from 'react';
import api from '../../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Switch } from '../../components/ui/switch';
import { Textarea } from '../../components/ui/textarea';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import { Mail, Settings, FileText, Send, RefreshCw } from 'lucide-react';
import { toast } from 'sonner';

function SettingsTab() {
  const [s, setS] = useState(null);
  const [saving, setSaving] = useState(false);
  const [testTo, setTestTo] = useState('');
  const [testing, setTesting] = useState(false);
  useEffect(() => { api.get('/admin/email/settings').then(r => setS(r.data)).catch(() => {}); }, []);
  const u = (k, v) => setS(p => ({ ...p, [k]: v }));
  const save = async () => { setSaving(true); try { await api.put('/admin/email/settings', s); toast.success('Email settings saved'); } catch { toast.error('Failed'); } finally { setSaving(false); } };
  const sendTest = async () => { setTesting(true); try { const r = await api.post('/admin/email/test', { to: testTo || undefined }); toast[r.data.success ? 'success' : 'error'](r.data.message); } catch { toast.error('Failed'); } finally { setTesting(false); } };

  if (!s) return <div className="flex items-center justify-center h-40"><div className="w-6 h-6 border-2 border-emerald-700 border-t-transparent rounded-full animate-spin" /></div>;

  return (
    <div className="space-y-6 max-w-2xl">
      <Card className="bg-white border border-slate-200 shadow-sm rounded-2xl">
        <CardHeader className="flex flex-row items-center justify-between pb-3">
          <CardTitle style={{ fontFamily: 'Outfit' }}>Email Configuration</CardTitle>
          <div className="flex items-center gap-2"><Label className="text-sm">Enabled</Label><Switch checked={s.enabled} onCheckedChange={v => u('enabled', v)} data-testid="email-enable" /></div>
        </CardHeader>
        <CardContent className="space-y-4">
          <div>
            <Label>Mail Driver</Label>
            <Select value={s.driver} onValueChange={v => u('driver', v)}>
              <SelectTrigger className="mt-1" data-testid="email-driver"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="console">Console (Log only)</SelectItem>
                <SelectItem value="smtp">Custom SMTP</SelectItem>
                <SelectItem value="sendgrid">SendGrid</SelectItem>
                <SelectItem value="resend">Resend</SelectItem>
                <SelectItem value="mailgun">Mailgun</SelectItem>
                <SelectItem value="ses">Amazon SES</SelectItem>
                <SelectItem value="postmark">Postmark</SelectItem>
                <SelectItem value="brevo">Brevo / Sendinblue</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div><Label>From Name</Label><Input data-testid="email-from-name" value={s.from_name || ''} onChange={e => u('from_name', e.target.value)} className="mt-1" /></div>
            <div><Label>From Email</Label><Input data-testid="email-from-email" value={s.from_email || ''} onChange={e => u('from_email', e.target.value)} className="mt-1" /></div>
            <div><Label>Reply-To</Label><Input value={s.reply_to || ''} onChange={e => u('reply_to', e.target.value)} className="mt-1" /></div>
            <div><Label>Admin Email</Label><Input value={s.admin_email || ''} onChange={e => u('admin_email', e.target.value)} className="mt-1" /></div>
          </div>
          {s.driver === 'smtp' && (
            <div className="space-y-4 pt-2 border-t border-slate-100">
              <p className="text-xs font-semibold text-slate-500 uppercase">SMTP Settings</p>
              <div className="grid grid-cols-2 gap-4">
                <div><Label>SMTP Host</Label><Input data-testid="smtp-host" value={s.smtp_host || ''} onChange={e => u('smtp_host', e.target.value)} className="mt-1" placeholder="smtp.gmail.com" /></div>
                <div><Label>SMTP Port</Label><Input type="number" value={s.smtp_port || 587} onChange={e => u('smtp_port', parseInt(e.target.value))} className="mt-1" /></div>
                <div><Label>Username</Label><Input value={s.smtp_username || ''} onChange={e => u('smtp_username', e.target.value)} className="mt-1" /></div>
                <div><Label>Password {s.has_smtp_password && <span className="text-emerald-600 text-xs">(set)</span>}</Label><Input type="password" value={s.smtp_password || ''} onChange={e => u('smtp_password', e.target.value)} className="mt-1" placeholder={s.has_smtp_password ? 'Leave blank to keep' : ''} /></div>
              </div>
              <div>
                <Label>Encryption</Label>
                <Select value={s.smtp_encryption || 'tls'} onValueChange={v => u('smtp_encryption', v)}>
                  <SelectTrigger className="mt-1 w-40"><SelectValue /></SelectTrigger>
                  <SelectContent><SelectItem value="none">None</SelectItem><SelectItem value="ssl">SSL</SelectItem><SelectItem value="tls">TLS</SelectItem></SelectContent>
                </Select>
              </div>
            </div>
          )}
          {['sendgrid', 'resend', 'mailgun', 'ses', 'postmark', 'brevo'].includes(s.driver) && (
            <div className="pt-2 border-t border-slate-100">
              <Label>API Key {s.has_api_key && <span className="text-emerald-600 text-xs">(set)</span>}</Label>
              <Input type="password" data-testid="email-api-key" value={s.api_key || ''} onChange={e => u('api_key', e.target.value)} className="mt-1" placeholder={s.has_api_key ? 'Leave blank to keep' : 'Paste API key'} />
            </div>
          )}
          <div className="flex gap-2">
            <Button onClick={save} disabled={saving} className="bg-emerald-700 hover:bg-emerald-800 text-white" data-testid="save-email-btn">{saving ? 'Saving...' : 'Save Settings'}</Button>
          </div>
        </CardContent>
      </Card>
      <Card className="bg-white border border-slate-200 shadow-sm rounded-2xl">
        <CardHeader><CardTitle style={{ fontFamily: 'Outfit' }}>Send Test Email</CardTitle></CardHeader>
        <CardContent className="flex gap-3 items-end">
          <div className="flex-1"><Label>To Email</Label><Input value={testTo} onChange={e => setTestTo(e.target.value)} placeholder="admin@jjuma.com" className="mt-1" data-testid="test-email-to" /></div>
          <Button onClick={sendTest} disabled={testing} className="bg-emerald-700 hover:bg-emerald-800 text-white" data-testid="send-test-email-btn"><Send size={14} className="mr-1" /> {testing ? 'Sending...' : 'Send Test'}</Button>
        </CardContent>
      </Card>
    </div>
  );
}

function TemplatesTab() {
  const [templates, setTemplates] = useState(null);
  const [saving, setSaving] = useState(false);
  useEffect(() => { api.get('/admin/email/templates').then(r => setTemplates(r.data)).catch(() => {}); }, []);
  const updateTpl = (key, field, val) => setTemplates(p => ({ ...p, [key]: { ...p[key], [field]: val } }));
  const save = async () => { setSaving(true); try { await api.put('/admin/email/templates', templates); toast.success('Templates saved'); } catch { toast.error('Failed'); } finally { setSaving(false); } };

  if (!templates) return <div className="flex items-center justify-center h-40"><div className="w-6 h-6 border-2 border-emerald-700 border-t-transparent rounded-full animate-spin" /></div>;

  return (
    <div className="space-y-4 max-w-2xl">
      <p className="text-sm text-slate-500">Edit email templates. Use variables like {'{{user_name}}'}, {'{{verification_code}}'}, {'{{reset_link}}'}, {'{{amount}}'}, {'{{currency}}'}, {'{{transaction_id}}'}, {'{{dashboard_link}}'}</p>
      {Object.entries(templates).map(([key, tpl]) => (
        <Card key={key} className="bg-white border border-slate-200 shadow-sm rounded-2xl">
          <CardContent className="p-4 space-y-3">
            <p className="text-xs font-semibold text-slate-500 uppercase">{key.replace(/_/g, ' ')}</p>
            <div><Label className="text-xs">Subject</Label><Input value={tpl.subject || ''} onChange={e => updateTpl(key, 'subject', e.target.value)} className="mt-1 text-sm" data-testid={`tpl-subject-${key}`} /></div>
            <div><Label className="text-xs">HTML Body</Label><Textarea value={tpl.body || ''} onChange={e => updateTpl(key, 'body', e.target.value)} className="mt-1 text-xs font-mono" rows={3} data-testid={`tpl-body-${key}`} /></div>
          </CardContent>
        </Card>
      ))}
      <Button onClick={save} disabled={saving} className="bg-emerald-700 hover:bg-emerald-800 text-white" data-testid="save-templates-btn">{saving ? 'Saving...' : 'Save All Templates'}</Button>
    </div>
  );
}

function LogsTab() {
  const [data, setData] = useState({ logs: [], total: 0, pages: 1 });
  const [page, setPage] = useState(1);
  const fetchLogs = useCallback(() => { api.get(`/admin/email/logs?page=${page}`).then(r => setData(r.data)).catch(() => {}); }, [page]);
  useEffect(() => { fetchLogs(); }, [fetchLogs]);
  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <span className="text-sm text-slate-500">{data.total} emails sent</span>
        <Button variant="outline" size="sm" onClick={fetchLogs} data-testid="refresh-email-logs"><RefreshCw size={14} className="mr-1" /> Refresh</Button>
      </div>
      <Card className="bg-white border border-slate-200 shadow-sm rounded-2xl">
        <CardContent className="p-0 overflow-x-auto">
          {data.logs.length === 0 ? (
            <div className="py-12 text-center"><Mail size={36} className="mx-auto text-slate-300 mb-3" /><p className="text-slate-500 text-sm">No email logs yet</p></div>
          ) : (
            <Table>
              <TableHeader><TableRow className="bg-slate-50">
                <TableHead className="text-xs font-semibold text-slate-500 uppercase">To</TableHead>
                <TableHead className="text-xs font-semibold text-slate-500 uppercase">Subject</TableHead>
                <TableHead className="text-xs font-semibold text-slate-500 uppercase">Driver</TableHead>
                <TableHead className="text-xs font-semibold text-slate-500 uppercase">Status</TableHead>
                <TableHead className="text-xs font-semibold text-slate-500 uppercase">Date</TableHead>
              </TableRow></TableHeader>
              <TableBody>
                {data.logs.map((l, i) => (
                  <TableRow key={i} className="hover:bg-slate-50/50" data-testid={`email-log-${i}`}>
                    <TableCell className="text-sm text-slate-700">{l.to}</TableCell>
                    <TableCell className="text-sm text-slate-700 max-w-[200px] truncate">{l.subject}</TableCell>
                    <TableCell className="text-xs capitalize">{l.driver}</TableCell>
                    <TableCell><span className={`text-xs px-2 py-0.5 rounded-full ${l.status === 'sent' ? 'bg-emerald-100 text-emerald-800' : l.status === 'console' ? 'bg-blue-100 text-blue-800' : 'bg-red-100 text-red-800'}`}>{l.status}</span></TableCell>
                    <TableCell className="text-xs text-slate-500">{new Date(l.created_at).toLocaleString()}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

export default function EmailSettings() {
  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-semibold text-slate-900 tracking-tight" style={{ fontFamily: 'Outfit' }}>Email Settings</h1>
        <p className="text-slate-500 text-sm mt-0.5">Configure SMTP, email templates, and view logs</p>
      </div>
      <Tabs defaultValue="settings">
        <TabsList className="mb-4" data-testid="email-tabs">
          <TabsTrigger value="settings" data-testid="email-tab-settings"><Settings size={14} className="mr-1" /> Settings</TabsTrigger>
          <TabsTrigger value="templates" data-testid="email-tab-templates"><FileText size={14} className="mr-1" /> Templates</TabsTrigger>
          <TabsTrigger value="logs" data-testid="email-tab-logs"><Mail size={14} className="mr-1" /> Logs</TabsTrigger>
        </TabsList>
        <TabsContent value="settings"><SettingsTab /></TabsContent>
        <TabsContent value="templates"><TemplatesTab /></TabsContent>
        <TabsContent value="logs"><LogsTab /></TabsContent>
      </Tabs>
    </div>
  );
}
